import { Mui, MuiIcons } from '@osu/react-ui';
import { ProgramBenefits } from '../ui';
import { activeStepMap } from '../../utils/stepHelpers';

/**
 * Template for "Scholars Invitation" step content
 * Receives pre-processed content and actions as props from step building logic
 * @param {Object} props

 * @param {string} [props.stepId] - Step identifier ('scholars-invitation' or 'scholars-program-invitation')
 * @param {Object} props.stepContent - Pre-processed content from step building
 * @param {Array} props.stepActions - Pre-processed actions from step building
 * @param {function} [props.onAction] - Action handler
 */
function InvitationTemplate({
  stepId = 'scholars-invitation',
  stepContent, // Pre-processed content from step building
  stepActions, // Pre-processed actions from step building
  onAction
}) {
  // Use the pre-processed content and actions passed as props
  const content = stepContent || {};
  const actions = stepActions || [];

  const handleAction = (action) => {
    if (onAction) {
      onAction(action.type, action);
    }
  };

  return (
    <Mui.Box>
      <Mui.Alert severity="info" sx={{ mb: 3 }}>
        {content.description}
      </Mui.Alert>

      {/* Main message from configurable constants */}
      {content.message && (
        <Mui.Typography variant="body1" paragraph>
          {content.message}
        </Mui.Typography>
      )}

      {content.additionalInfo && (
        <Mui.Typography variant="body1" paragraph>
          {content.additionalInfo}
        </Mui.Typography>
      )}

      {/* Program Benefits for scholars invitation */}
      {stepId === 'scholars-invitation' && (
        <ProgramBenefits />
      )}

      {/* Action buttons */}
      {stepId !== activeStepMap.SCHOLARS_PREFERENCES && <Actions
         actions={actions} 
         onClick={handleAction} 
         sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}
        />}

      {/* Contact information from configurable constants */}
      {content.contactInfo && (
        <Mui.Box sx={{ mt: 3, p: 2, backgroundColor: '#e8f5e8', borderRadius: 1, border: '1px solid #c8e6c9' }}>
          <Mui.Typography variant="body2" color="success.main">
            <strong>Questions?</strong>
          </Mui.Typography>
          <Mui.Typography variant="body2" sx={{ mt: 1 }}>
            {content.contactInfo}
          </Mui.Typography>
        </Mui.Box>
      )}

      {/* Next steps information */}
      <Mui.Box sx={{ mt: 3 }}>
        <Mui.Card variant="outlined" sx={{ p: 2, backgroundColor: '#f3e5f5' }}>
          <Mui.Typography variant="h6" gutterBottom color="secondary">
            What Happens Next?
          </Mui.Typography>
          <Mui.Typography variant="body2" paragraph>
            After accepting your invitation, you'll be able to explore the 16 different Scholars programs
            and submit your preferences for program placement.
          </Mui.Typography>
          <Mui.List dense>
            <Mui.ListItem>
              <Mui.ListItemIcon>
                <MuiIcons.Explore color="secondary" />
              </Mui.ListItemIcon>
              <Mui.ListItemText
                primary="Explore Programs"
                secondary="Learn about each of the 16 Scholars programs"
              />
            </Mui.ListItem>
            <Mui.ListItem>
              <Mui.ListItemIcon>
                <MuiIcons.Assignment color="secondary" />
              </Mui.ListItemIcon>
              <Mui.ListItemText
                primary="Submit Preferences"
                secondary="Rank your preferred programs for placement"
              />
            </Mui.ListItem>
            <Mui.ListItem>
              <Mui.ListItemIcon>
                <MuiIcons.Notifications color="secondary" />
              </Mui.ListItemIcon>
              <Mui.ListItemText
                primary="Be reviewed for your ranked programs"
                secondary="Get notified of your program assignment"
              />
            </Mui.ListItem>
          </Mui.List>
          {stepId === activeStepMap.SCHOLARS_PREFERENCES && <Actions 
            actions={actions} 
            onClick={handleAction} 
            divider
            sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}
          />}
        </Mui.Card>
      </Mui.Box>
    </Mui.Box>
  );
}

const Actions = ({
  actions,
  onClick,
  sx,
  divider = false
}) => {
  if (!actions || actions.length === 0) {
    return false;
  }
  return <Mui.Box sx={sx}>
    {divider && <Mui.Divider sx={{ width: '100%' }} />}
    {actions.map((action, index) => {
      const styles = [{ minWidth: '150px' }];
      if(action.sx) {
        styles.push(action.sx);
      }
      return <Mui.Button
        key={index}
        variant={action.variant || 'contained'}
        color={action.color || 'primary'}
        size="large"
        onClick={() => onClick(action)}
        sx={styles}
        aria-label={`${action.label} for Scholars invitation`}
      >
        {action.label}
      </Mui.Button>
    })}
  </Mui.Box>
}

export default InvitationTemplate;